/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Required to use React components.
 */
import { FC, useCallback, useContext, useState } from 'react';
import { ObjectInspector } from 'react-inspector';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { useFormState } from '@ngt/forms-core';
import CollapsiblePanel from '@/components/layouts/CollapsiblePanel';
import ChosenThemeContext from '@/contexts/styles/ChosenThemeContext';
import { Box } from '@mui/material';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/**
 * This interface defines the properties for the FormErrors component.
 */
export interface IFormErrorsProps {

}

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

/**
 * This component provides context for the patient management system.
 * @param param0 component properties.
 */
const FormErrors: FC<IFormErrorsProps> = ({
}) => {
    return (
        <CollapsiblePanel
            ComponentProps={{
                title: 'Errors'
            }}
        >
            <FormErrorsInternal />
        </CollapsiblePanel>
    );
}

const FormErrorsInternal: FC<IFormErrorsProps> = ({
}) => {
    const { errors } = useFormState({ errors: true });
    const theme = useContext(ChosenThemeContext);

    return (
        <Box
            sx={{
                '& ol > li > div': {
                    display: 'flex'
                },
                '& ol > li > div > span:nth-of-type(1)': {
                    mr: 1
                },
                '& ol > li > div > span:nth-of-type(2)': {
                    display: 'flex'
                },
                '& ol > li > div > span:nth-of-type(2) > span:nth-of-type(2)': {
                    mr: 1
                },
                '& > ol > li > div': {
                    display: 'none'
                },
                '& > ol > li > ol': {
                    padding: 2
                }
            }}
        >
            <ObjectInspector
                theme={theme === 'dark' ? 'chromeDark' : 'chromeLight'}
                expandLevel={2}
                data={errors}
            />
        </Box>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default FormErrors;